// frontend/packages/business-console/src/components/content/FrontmatterField.jsx

import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
  hasPath,
  getAtPath,
  setAtPath,
  safeString,
} from '../../utils/contentEditorHelpers';
import MediaField from './MediaField';
import DocumentField from './DocumentField';
import ArrayObjectField from './ArrayObjectField';
import HelpLink from '../help/HelpLink';

// Field-path → help topic. Only applies at the object-group level so help
// surfaces once per section, not on every leaf field.
const GROUP_HELP_TOPICS = {
  hero: 'edit-hero',
  contact: 'update-contact',
  brand: 'change-logo',
  marketing: 'settings-overview',
  sections: 'edit-sections',
};

// ---------------------------------------------------------------------------
// Label humanization
// ---------------------------------------------------------------------------

const LABEL_OVERRIDES = {
  ogImage: 'Social Sharing Image',
  ogImageAlt: 'Social Image Description',
  noindex: 'Hide from Search Engines',
  noFollow: 'Block Search Engine Links',
  canonical: 'Page Address Override',
  gaTrackingId: 'Google Analytics ID',
  gtmId: 'Google Tag Manager ID',
  pixelId: 'Meta Pixel ID',
  adwordsId: 'Google Ads ID',
  adwordsConversionLabel: 'Google Ads Label',
  bingUetTag: 'Bing Tracking Tag',
  callRailSwapKey: 'CallRail Key',
  intakeUrl: 'Form Submission URL',
  nativePostUrl: 'Contact Form URL',
  successRedirect: 'Form Success Page',
  embedIntake: 'Embed External Form',
  bookingUrl: 'Booking Platform Link',
  elfsightWidgetId: 'Reviews Widget ID',
  elfsightContactAppId: 'Contact Widget ID',
  primaryCta: 'Primary Button',
  secondaryCta: 'Secondary Button',
  eyebrow: 'Small Label Above Headline',
  subheadline: 'Supporting Text',
  backgroundImage: 'Background Image',
  backgroundPosition: 'Background Position',
  bioShort: 'Short Bio',
  profileEnabled: 'Show Profile Page',
  showOnAbout: 'Show on About Page',
  coverImage: 'Cover Image',
  coverImageAlt: 'Cover Image Description',
  coverImageCaption: 'Cover Image Caption',
  defaultHeroBackground: 'Default Hero Background',
  defaultHeroPosition: 'Default Hero Position',
  defaultOgImage: 'Default Social Image',
  defaultDescription: 'Default Search Description',
  titleTemplate: 'Page Title Format',
  metaTitleSuffix: 'Page Title Suffix',
  indexSite: 'Allow Search Engines',
  priceRange: 'Price Range',
  foundingDate: 'Year Founded',
  siteName: 'Business Name',
  legalName: 'Legal Business Name',
  imageAlt: 'Image Description',
  trustLine: 'Trust Badge Text',
  serviceRadiusMiles: 'Service Area Radius (miles)',
};

const LABEL_ABBREVIATIONS = {
  url: 'URL',
  urls: 'URLs',
  cta: 'Button',
  id: 'ID',
  seo: 'Search Settings',
  faq: 'FAQ',
  faqs: 'FAQs',
  api: 'API',
  html: 'HTML',
  css: 'CSS',
  img: 'Image',
  src: 'Source',
  alt: 'Description',
};

function humanizeFieldLabel(path, required) {
  const key = path.includes('.') ? path.split('.').pop() : path;

  if (LABEL_OVERRIDES[key]) {
    return `${LABEL_OVERRIDES[key]}${required ? ' *' : ''}`;
  }

  const words = key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .toLowerCase()
    .split(/\s+/);
  const label = words
    .map(
      (w) => LABEL_ABBREVIATIONS[w] || w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');
  return `${label}${required ? ' *' : ''}`;
}

// ---------------------------------------------------------------------------
// Description parsing — supports functional markers + helper text
// ---------------------------------------------------------------------------

function isMediaSpec(spec) {
  const desc = String(spec?.description || '')
    .trim()
    .toLowerCase();
  return (
    desc.startsWith('upryzze:media:image') ||
    desc.startsWith('upryzze:media:video')
  );
}

function mediaTypeFromSpec(spec) {
  const desc = String(spec?.description || '')
    .trim()
    .toLowerCase();
  if (desc.startsWith('upryzze:media:video')) return 'video';
  return 'image';
}

function isDocumentSpec(spec) {
  const desc = String(spec?.description || '')
    .trim()
    .toLowerCase();
  return desc.startsWith('upryzze:media:document');
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function isInternalField(spec) {
  const desc = String(spec?.description || '')
    .trim()
    .toLowerCase();
  return desc === 'upryzze:internal';
}

function getHelperText(spec) {
  const desc = String(spec?.description || '');
  if (!desc) return '';
  const pipeIdx = desc.indexOf('|');
  if (pipeIdx > 0) return desc.slice(pipeIdx + 1).trim();
  if (desc.startsWith('upryzze:')) return '';
  return desc;
}

// ---------------------------------------------------------------------------
// Fallback media detection by field name
// ---------------------------------------------------------------------------

const IMAGE_NAME_PATTERN = /image$/i;
const VIDEO_NAME_PATTERN = /video$/i;

function inferMediaTypeByName(path) {
  if (!path) return null;
  const key = String(path).includes('.')
    ? String(path).split('.').pop()
    : String(path);
  if (VIDEO_NAME_PATTERN.test(key)) return 'video';
  if (IMAGE_NAME_PATTERN.test(key)) return 'image';
  return null;
}

function resolveMediaType(spec, path) {
  if (isMediaSpec(spec)) return mediaTypeFromSpec(spec);
  if (spec?.type === 'string') return inferMediaTypeByName(path);
  return null;
}

function isArrayOfObjects(spec) {
  if (spec?.type !== 'array') return false;
  const items = spec?.items;
  if (!items) return false;
  if (items.type === 'object' && items.fields) return true;
  if (items.type === 'union' && Array.isArray(items.options)) return true;
  if (items.discriminator) return true;
  return false;
}

function ArrayStringField({ label, value, path, frontmatterObj, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const arr = Array.isArray(value) ? value : [];

  const handleAdd = () => {
    const val = inputValue.trim();
    if (val) {
      onChange(setAtPath(frontmatterObj, path, [...arr, val]));
      setInputValue('');
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
        {label}
      </Typography>
      {arr.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
          {arr.map((item, i) => (
            <Chip
              key={`${path}-${i}`}
              label={String(item)}
              onDelete={() => {
                const next = arr.filter((_, idx) => idx !== i);
                onChange(setAtPath(frontmatterObj, path, next));
              }}
              size="small"
            />
          ))}
        </Box>
      ) : null}
      <Stack direction="row" spacing={1}>
        <TextField
          placeholder="Add item..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          size="small"
          sx={{ flex: 1 }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!inputValue.trim()}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}

export default function FrontmatterField({
  path,
  spec,
  frontmatterObj,
  showAllOptional,
  onChange,
  onPickMedia,
}) {
  const value = useMemo(
    () => getAtPath(frontmatterObj, path),
    [frontmatterObj, path]
  );
  const exists = useMemo(
    () => hasPath(frontmatterObj, path),
    [frontmatterObj, path]
  );

  if (isInternalField(spec)) return null;
  if (!showAllOptional && !spec?.required && !exists) return null;

  const label = humanizeFieldLabel(path, Boolean(spec?.required));
  const helperText = getHelperText(spec);

  // Document link (PDF / Office) with upload; fills an empty sibling `size`
  // field (e.g. documents.files[].size) from the uploaded file.
  if (spec?.type === 'string' && isDocumentSpec(spec)) {
    const parent = String(path).includes('.')
      ? String(path).split('.').slice(0, -1).join('.')
      : '';
    const sizePath = parent ? `${parent}.size` : 'size';
    return (
      <DocumentField
        label={label}
        value={typeof value === 'string' ? value : ''}
        required={Boolean(spec?.required)}
        helperText={helperText}
        onChangeRaw={(nextValue) =>
          onChange(setAtPath(frontmatterObj, path, nextValue))
        }
        onUploaded={(asset) => {
          const size = formatBytes(asset?.fileSize);
          const url = asset?.publicUrl || asset?.url || '';
          let next = setAtPath(frontmatterObj, path, url);
          if (size && !getAtPath(frontmatterObj, sizePath))
            next = setAtPath(next, sizePath, size);
          onChange(next);
        }}
      />
    );
  }

  // Media field specialization
  const detectedMediaType = resolveMediaType(spec, path);
  if (spec?.type === 'string' && detectedMediaType) {
    return (
      <MediaField
        label={label}
        value={typeof value === 'string' ? value : ''}
        required={Boolean(spec?.required)}
        mediaType={detectedMediaType}
        helperText={helperText}
        onChoose={() => onPickMedia?.({ path })}
        onClear={() => onChange(setAtPath(frontmatterObj, path, ''))}
        onChangeRaw={(nextValue) =>
          onChange(setAtPath(frontmatterObj, path, nextValue))
        }
      />
    );
  }

  // Object group
  if (spec?.type === 'object' && spec?.fields) {
    if (!showAllOptional && !spec.required) {
      const anyChild = Object.keys(spec.fields).some((k) =>
        hasPath(frontmatterObj, `${path}.${k}`)
      );
      if (!anyChild) return null;
    }

    const groupLabel = LABEL_OVERRIDES[path.split('.').pop()] || label;
    const helpTopic = GROUP_HELP_TOPICS[path.split('.').pop()];

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 0.5 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {groupLabel}
          </Typography>
          {helpTopic && <HelpLink topic={helpTopic} />}
        </Stack>
        {helperText && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1.5, opacity: 0.6 }}
          >
            {helperText}
          </Typography>
        )}
        <Stack spacing={1.25}>
          {Object.entries(spec.fields).map(([childKey, childSpec]) => (
            <FrontmatterField
              key={`${path}.${childKey}`}
              path={`${path}.${childKey}`}
              spec={childSpec}
              frontmatterObj={frontmatterObj}
              showAllOptional={showAllOptional}
              onChange={onChange}
              onPickMedia={onPickMedia}
            />
          ))}
        </Stack>
      </Paper>
    );
  }

  // Boolean — use friendly labels for inverted booleans
  if (spec?.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(value)}
            onChange={(e) =>
              onChange(setAtPath(frontmatterObj, path, e.target.checked))
            }
          />
        }
        label={
          <Box>
            <Typography variant="body2">{label}</Typography>
            {helperText && (
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {helperText}
              </Typography>
            )}
          </Box>
        }
      />
    );
  }

  // Date
  if (spec?.type === 'date') {
    return (
      <TextField
        label={label}
        type="date"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) =>
          onChange(setAtPath(frontmatterObj, path, e.target.value))
        }
        InputLabelProps={{ shrink: true }}
        fullWidth
        size="small"
        helperText={helperText || undefined}
      />
    );
  }

  // Number
  if (spec?.type === 'number') {
    const num =
      typeof value === 'number'
        ? value
        : value === undefined || value === ''
          ? ''
          : Number(value);

    return (
      <TextField
        label={label}
        type="number"
        value={Number.isFinite(num) ? num : ''}
        onChange={(e) =>
          onChange(
            setAtPath(
              frontmatterObj,
              path,
              e.target.value === '' ? undefined : Number(e.target.value)
            )
          )
        }
        fullWidth
        size="small"
        helperText={helperText || undefined}
      />
    );
  }

  // Enum
  if (spec?.type === 'enum' && Array.isArray(spec?.values)) {
    return (
      <TextField
        label={label}
        select
        SelectProps={{ native: true }}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) =>
          onChange(setAtPath(frontmatterObj, path, e.target.value))
        }
        fullWidth
        size="small"
        helperText={helperText || undefined}
      >
        <option value="">(select)</option>
        {spec.values.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </TextField>
    );
  }

  // Array of strings
  if (spec?.type === 'array' && spec?.items?.type === 'string') {
    return (
      <ArrayStringField
        label={label}
        value={value}
        path={path}
        frontmatterObj={frontmatterObj}
        onChange={onChange}
      />
    );
  }

  // Array of objects or union
  if (isArrayOfObjects(spec)) {
    return (
      <ArrayObjectField
        label={label}
        path={path}
        spec={spec}
        frontmatterObj={frontmatterObj}
        showAllOptional={showAllOptional}
        onChange={onChange}
        onPickMedia={onPickMedia}
      />
    );
  }

  // Default: string
  return (
    <TextField
      label={label}
      value={safeString(value)}
      onChange={(e) =>
        onChange(setAtPath(frontmatterObj, path, e.target.value))
      }
      fullWidth
      size="small"
      helperText={helperText || undefined}
    />
  );
}
